import React, { useEffect, useState } from 'react';
import {
    Button,
    Alert,
    AlertTitle,
    Stack,
    // wrap
} from '@mui/material';
import ConfirmationDialog from '../../../../main/pages/settings/notifications/ConfirmationDialog';
import { useFormContext } from 'react-hook-form';
import useIsMobile from '../../../../hooks/useIsMobile';

const ProducerPickupSequenceVerification = ({ sequenceName, amountName }) => {
    const isMobile = useIsMobile();
    const [expectedAmountDialogOpen, setExpectedAmountDialogOpen] = useState(false);
    const [producerSequenceDialogOpen, setProducerSequenceDialogOpen] = useState(false);
    const [producerSequenceProrateDialogOpen, setProducerSequenceProrateDialogOpen] = useState(false);
    const { watch, setValue, setError, clearErrors } = useFormContext();
    const sequenceWatcher = watch(sequenceName);
    const amountWatcher = watch(amountName);
    const producerPickupSequenceTotal = sequenceWatcher?.reduce((acc, seq) => Number(seq.expected_amount) + acc, 0);
    const amount = Number(amountWatcher ?? 0);
    const shouldShowAlert = amount !== producerPickupSequenceTotal && !!sequenceWatcher?.length && amountWatcher != null;

    useEffect(() => {
        if (shouldShowAlert) {
            setError(amountName, { type: 'required', message: 'Amount must match producer order' });
        } else {
            clearErrors(amountName);
        }
    }, [shouldShowAlert]);

    const handleSetExpectedAmount = () => {
        setValue(amountName, producerPickupSequenceTotal);
    };

    const handleCalculatePickupSequenceAmounts = () => {
        const avg = Math.round(amount / sequenceWatcher?.length);
        const diff = amount - avg * sequenceWatcher.length; // Geting rounding descrepency

        setValue(
            sequenceName,
            sequenceWatcher.map((pickup, i) => ({
                ...pickup,
                expected_amount: i === sequenceWatcher.length - 1 ? avg + diff : avg, // Adjust last average to account for any rounding errors
            }))
        );
    };

    const handleCalculatePickupProratedSequenceAmounts = () => {
        const scaledAmount = Math.round(amount * 100);

        const originalAmount = sequenceWatcher?.reduce((acc, pickup) => acc + Number(pickup.expected_amount), 0);
        const ratios = sequenceWatcher?.map((pickup) => Number(pickup.expected_amount) / originalAmount);

        const newAmounts = ratios.map((ratio) => Math.floor(ratio * scaledAmount));
        const newTotal = newAmounts.reduce((acc, amt) => acc + amt, 0);

        const remainder = scaledAmount - newTotal;
        const fractionalParts = ratios
            .map((ratio, index) => ({
                index,
                fraction: ratio * scaledAmount - Math.floor(ratio * scaledAmount),
            }))
            .sort((a, b) => b.fraction - a.fraction);

        const finalAmounts = [...newAmounts];
        for (let i = 0; i < remainder; i++) {
            finalAmounts[fractionalParts[i % fractionalParts.length].index] += 1;
        }

        setValue(
            sequenceName,
            sequenceWatcher?.map((pickup, i) => ({
                ...pickup,
                expected_amount: +(finalAmounts[i] / 100).toFixed(2),
            }))
        );
    };

    return (
        <>
            {shouldShowAlert && (
                <Alert
                    severity="warning"
                    sx={{
                        ...(isMobile && {
                            maxWidth: '100%',
                            overflow: 'hidden',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            '& .MuiAlert-action': { width: '100%', pl: 0, pt: 1 },
                        }),
                    }}
                    action={
                        <Stack direction={isMobile ? 'column' : 'row'} spacing={1}>
                            <Button color="inherit" variant="outlined" size="small" onClick={() => (Number(amountWatcher) ? setExpectedAmountDialogOpen(true) : handleSetExpectedAmount())}>
                                Set Expected Amount
                            </Button>
                            <Button color="inherit" variant="outlined" size="small" onClick={() => (producerPickupSequenceTotal ? setProducerSequenceDialogOpen(true) : handleCalculatePickupSequenceAmounts())}>
                                Average Producer Order Amount
                            </Button>
                            <Button color="inherit" variant="outlined" size="small" onClick={() => (producerPickupSequenceTotal ? setProducerSequenceProrateDialogOpen(true) : handleCalculatePickupProratedSequenceAmounts())}>
                                Prorate Producer Order Amount
                            </Button>
                        </Stack>
                    }
                >
                    <AlertTitle>
                        Expected amount ({amountWatcher ?? 0}) does not match producer order ({producerPickupSequenceTotal}).
                    </AlertTitle>
                    <br />
                    Please select an action to correct this.
                    <br />
                    <strong>Set Expected Amount</strong> will replace the current expected amount with the sum of the producer order amounts.
                    <br />
                    <strong>Average Producer Order Amount</strong> will replace all producer order amounts with an average based on the current expected amount.
                </Alert>
            )}
            <ConfirmationDialog
                open={expectedAmountDialogOpen}
                message={`This action will replace the current expected amount (${amountWatcher}) with the total pickup order (${producerPickupSequenceTotal}). Are you sure you want to proceed?`}
                onDialogConfirmAction={() => {
                    handleSetExpectedAmount();
                    setExpectedAmountDialogOpen(false);
                }}
                onDialogCancelAction={() => setExpectedAmountDialogOpen(false)}
            />
            <ConfirmationDialog
                open={producerSequenceDialogOpen}
                message={`This action will replace all amounts in the pickup order with an average based on the current expected amount (${amountWatcher}). Are you sure you want to proceed?`}
                onDialogConfirmAction={() => {
                    handleCalculatePickupSequenceAmounts();
                    setProducerSequenceDialogOpen(false);
                }}
                onDialogCancelAction={() => setProducerSequenceDialogOpen(false)}
            />
            <ConfirmationDialog
                open={producerSequenceProrateDialogOpen}
                message={`This action will replace all amounts in the pickup order with a prorated amount based on the current expected amount (${amountWatcher}). Are you sure you want to proceed?`}
                onDialogConfirmAction={() => {
                    handleCalculatePickupProratedSequenceAmounts();
                    setProducerSequenceProrateDialogOpen(false);
                }}
                onDialogCancelAction={() => setProducerSequenceProrateDialogOpen(false)}
            />
        </>
    );
};

export default ProducerPickupSequenceVerification;
